import React, { useRef, useState, useCallback, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useSidebar, useAuth } from '@/context';
import { getGroupedNavItems, type NavIconKey } from '@/config/navigation';
import { useDataQualityOpenCount } from '@/features/data-quality';

const SidebarWrapper = styled.aside<{ $collapsed: boolean; $mobileOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${({ $collapsed }) => $collapsed ? '80px' : '280px'};
  background: ${({ theme }) => theme.colors.sidebarBgGradient};
  display: flex;
  flex-direction: column;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  transition: width ${({ theme }) => theme.transitions.normal}, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid ${({ theme }) => theme.colors.sidebarBorder};
  /* Subtle pattern overlay */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 80%, rgba(255, 107, 53, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 179, 71, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    /* Above the header (sticky) so the open drawer covers it, but below a
       real Modal — matches theme.zIndex ordering. */
    z-index: ${({ theme }) => theme.zIndex.dropdown + 1};
    transform: translateX(${({ $mobileOpen }) => $mobileOpen ? '0' : '-100%'});
    width: 280px;
    box-shadow: ${({ $mobileOpen }) => $mobileOpen ? '4px 0 24px rgba(0, 0, 0, 0.3)' : 'none'};
  }
  
  @media (max-width: 480px) {
    width: 85vw;
    max-width: 300px;
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 107, 53, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 1024px) {
    display: flex;
  }
`;

const Logo = styled.div<{ $collapsed: boolean }>`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.sidebarBorder};
  position: relative;
  
  h1 {
    font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
    font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
    background: linear-gradient(135deg, #FF6B35 0%, #FFB347 50%, #FFD700 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    white-space: nowrap;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    transform: translateX(${({ $collapsed }) => $collapsed ? '-10px' : '0'});
    transition: all ${({ theme }) => theme.transitions.normal};
  }
  
  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
    
    h1 {
      opacity: 1;
      transform: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md};
  }
`;

const LogoIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
  }
`;

const NavScrollWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

const NavSection = styled.nav`
  height: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 107, 53, 0.3);
    border-radius: 100px;
  }
`;

// Scroll affordance: fades in at the top/bottom edge whenever there is more
// nav content hidden in that direction, so a long grouped nav list never
// looks like it ends at the visible fold with no hint of more below.
const ScrollFade = styled.div<{ $edge: 'top' | 'bottom'; $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 28px;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${({ theme }) => theme.transitions.fast};
  ${({ $edge, theme }) =>
    $edge === 'top'
      ? css`
          top: 0;
          background: linear-gradient(180deg, ${theme.colors.sidebarBg} 0%, transparent 100%);
        `
      : css`
          bottom: 0;
          background: linear-gradient(0deg, ${theme.colors.sidebarBg} 0%, transparent 100%);
        `}
`;

const NavGroupHeader = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.sidebarTextMuted};
  font-size: 0.6875rem;
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};

  &:hover {
    color: ${({ theme }) => theme.colors.sidebarText};
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
`;

const GroupDivider = styled.div`
  height: 1px;
  margin: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.sidebarBorder};
`;

const NavItemLink = styled(NavLink)<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 12px ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.sidebarText};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  position: relative;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  
  &:hover {
    background: ${({ theme }) => theme.colors.sidebarHover};
    color: white;
  }
  
  &.active {
    background: ${({ theme }) => theme.colors.sidebarActiveGradient};
    color: white;
    box-shadow: 0 4px 16px rgba(255, 107, 53, 0.25);
    
    /* Active indicator */
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 60%;
      background: white;
      border-radius: 0 4px 4px 0;
    }
  }
  
  span {
    white-space: nowrap;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    transform: translateX(${({ $collapsed }) => $collapsed ? '-10px' : '0'});
    transition: all ${({ theme }) => theme.transitions.fast};
  }
  
  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

const CollapseButton = styled.button<{ $collapsed: boolean }>`
  position: absolute;
  top: 50%;
  right: -14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  z-index: 10;
  border: 2px solid ${({ theme }) => theme.colors.background};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(255, 107, 53, 0.4);
  }
  
  svg {
    width: 14px;
    height: 14px;
    transition: transform ${({ theme }) => theme.transitions.fast};
    transform: rotate(${({ $collapsed }) => $collapsed ? '180deg' : '0deg'});
  }
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const Overlay = styled.div<{ $visible: boolean }>`
  display: none;
  
  @media (max-width: 1024px) {
    display: ${({ $visible }) => $visible ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: ${({ theme }) => theme.zIndex.dropdown};
  }
`;

const SidebarFooter = styled.div<{ $collapsed: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.sidebarBorder};
  
  ${({ $collapsed }) => $collapsed && css`
    display: flex;
    justify-content: center;
  `}
`;

const NavItemBadge = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #dc2626;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
`;

const VersionBadge = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => $collapsed ? 'none' : 'inline-flex'};
  padding: 4px 12px;
  background: rgba(255, 179, 71, 0.1);
  border: 1px solid rgba(255, 179, 71, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: #D4AF37;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const UserSection = styled.div<{ $collapsed: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.sidebarBorder};
  
  ${({ $collapsed }) => $collapsed && css`
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`;

const UserInfo = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  ${({ $collapsed }) => $collapsed && css`
    justify-content: center;
  `}
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  flex-shrink: 0;
`;

const UserDetails = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => $collapsed ? 'none' : 'flex'};
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
`;

const UserName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.sidebarText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRoleBadge = styled.span<{ $role: string }>`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $role }) => {
    switch ($role) {
      case 'owner':   return 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)';
      case 'admin':   return 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)';
      case 'manager': return 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)';
      case 'staff':   return 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)';
      case 'viewer':  return 'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)';
      default: return 'linear-gradient(135deg, #9E9E9E 0%, #757575 100%)';
    }
  }};
  color: white;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  text-transform: capitalize;
  width: fit-content;
`;

const SignOutButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  padding: 10px ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: rgba(255, 107, 53, 0.1);
  color: ${({ theme }) => theme.colors.sidebarText};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  
  &:hover {
    background: rgba(255, 107, 53, 0.2);
    color: #FF6B35;
  }
  
  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
  
  span {
    display: ${({ $collapsed }) => $collapsed ? 'none' : 'inline'};
    white-space: nowrap;
  }
  
  ${({ $collapsed }) => $collapsed && css`
    justify-content: center;
    padding: 10px;
  `}
`;

// -----------------------------------------------------------------------
// Icon set — keyed by NavIconKey (src/config/navigation.ts), so the nav
// data (paths/labels/order per role+staff_type) stays framework-agnostic.
// -----------------------------------------------------------------------

const ICONS: Record<NavIconKey, React.ReactNode> = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  ),
  myDay: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  students: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  myStudents: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  ),
  staff: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  bookings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  reschedules: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  ),
  leaves: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  invoices: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  expenses: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2H10a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  paymentsDue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  memberships: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3z" />
    </svg>
  ),
  subjects: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  packages: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  reportsGrades: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  requests: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M9 15l2 2 4-4" />
    </svg>
  ),
  attendance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  dataQuality: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

export const Sidebar: React.FC = () => {
  const { isCollapsed, isMobileOpen, toggleCollapse, closeMobile, isGroupExpanded, toggleGroup } = useSidebar();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollRef = useRef<HTMLElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const updateScrollFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowTopFade(el.scrollTop > 4);
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  const navScrollRef = useCallback(
    (el: HTMLElement | null) => {
      scrollRef.current = el;
      if (el) updateScrollFade();
    },
    [updateScrollFade],
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // 11E.5/11F.2: config-driven, grouped nav items for this user's role +
  // staff_type (src/config/navigation.ts) — server-side RBAC gates every
  // route regardless of what's shown here (see backend/app/core/auth/scoping.py).
  const groupedNav = user?.role ? getGroupedNavItems(user.role, user.staff_type ?? null) : [];

  // 11F.1 — open-count badge on the Data Quality nav item. Only fires the
  // request for owner/admin (the only roles the endpoint doesn't 403 for).
  const dataQualityOpenCount = useDataQualityOpenCount(
    user?.role === 'owner' || user?.role === 'admin',
  );

  // Re-check fade visibility after any render that could change content
  // height (group expand/collapse, icon-rail collapse, nav data change).
  useEffect(() => {
    const frame = requestAnimationFrame(updateScrollFade);
    return () => cancelAnimationFrame(frame);
  });

  const getUserInitials = () => {
    if (!user) return '?';
    // Use email prefix until Phase 2 Staff profiles land
    return (user.email?.split('@')[0]?.[0] || 'U').toUpperCase();
  };

  return (
    <>
      <Overlay $visible={isMobileOpen} onClick={closeMobile} />
      <SidebarWrapper
        $collapsed={isCollapsed}
        $mobileOpen={isMobileOpen}
        data-testid="candeza-sidebar"
      >
        <MobileCloseButton onClick={closeMobile} aria-label="Close menu">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </MobileCloseButton>
        <Logo $collapsed={isCollapsed}>
          <LogoIcon>
            <img src="/media/icons/Candeza_logo_dark.png" alt="Candeza" />
          </LogoIcon>
          <h1>Candeza</h1>
        </Logo>

        <NavScrollWrapper>
          <NavSection
            ref={navScrollRef}
            data-testid="candeza-sidebar-nav"
            onScroll={updateScrollFade}
          >
            {groupedNav.map(({ group, items }, groupIdx) => {
              // Icon-rail collapse has no room for a header to toggle, so it
              // always shows every item — never hide a reachable route with
              // no affordance to reveal it again.
              const expanded = isCollapsed || isGroupExpanded(group.key);
              return (
                <React.Fragment key={group.key}>
                  {groupIdx > 0 && isCollapsed && <GroupDivider />}
                  <NavGroupHeader
                    type="button"
                    $collapsed={isCollapsed}
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={expanded}
                    data-testid={`candeza-nav-group-${group.key}`}
                  >
                    {group.label}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </NavGroupHeader>
                  {expanded &&
                    items.map((item) => (
                      <NavItemLink
                        key={item.path}
                        to={item.path}
                        $collapsed={isCollapsed}
                        onClick={closeMobile}
                        className={location.pathname === item.path ? 'active' : ''}
                        data-testid={`candeza-nav-item-${item.icon}`}
                      >
                        {ICONS[item.icon]}
                        <span>{item.label}</span>
                        {item.icon === 'dataQuality' && dataQualityOpenCount !== '0' && (
                          <NavItemBadge data-testid="candeza-nav-data-quality-badge">
                            {dataQualityOpenCount}
                          </NavItemBadge>
                        )}
                      </NavItemLink>
                    ))}
                </React.Fragment>
              );
            })}
          </NavSection>
          <ScrollFade $edge="top" $visible={showTopFade} />
          <ScrollFade $edge="bottom" $visible={showBottomFade} />
        </NavScrollWrapper>

        <UserSection $collapsed={isCollapsed}>
          <UserInfo $collapsed={isCollapsed}>
            <UserAvatar>{getUserInitials()}</UserAvatar>
            <UserDetails $collapsed={isCollapsed}>
              <UserName>{user?.email?.split('@')[0] || 'User'}</UserName>
              {user?.role && <UserRoleBadge $role={user.role}>{user.role}</UserRoleBadge>}
            </UserDetails>
          </UserInfo>
          <SignOutButton $collapsed={isCollapsed} onClick={handleSignOut}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>Sign Out</span>
          </SignOutButton>
        </UserSection>

        <SidebarFooter $collapsed={isCollapsed}>
          <VersionBadge $collapsed={isCollapsed}>v1.0.0</VersionBadge>
        </SidebarFooter>

        <CollapseButton
          $collapsed={isCollapsed}
          onClick={toggleCollapse}
          data-testid="candeza-sidebar-collapse"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </CollapseButton>
      </SidebarWrapper>
    </>
  );
};
